import apiService from '@/utils/api-client'
import { sendFormatter } from "@/utils/text-formatter";
import analytics from "@/store/modules/analytics";
import appConfig from '@/utils/appConfig'
import { featureAccess } from '@/utils/accesses'

const getFinalBaseUrl = (explicitUrl) => {
  if (explicitUrl) return explicitUrl;
  if (featureAccess('MULTI_LABELING')) return appConfig.VUE_APP_WALLET_LABEL_API_URL;
  return null;
}

export default {
  // eslint-disable-next-line no-unused-vars
  updateWallet({ commit }, { baseUrl, ...data }) {
    return apiService.put(`updateWallet`, data, { baseURL: getFinalBaseUrl(baseUrl) })
  },

  // eslint-disable-next-line no-unused-vars
  updateCluster({ commit }, { baseUrl, ...data }) {
    return apiService.put('updateCluster', data, { baseURL: getFinalBaseUrl(baseUrl) })
  },

  async getUploadConflicts({ commit }, { baseUrl, ...params }) {
    try {
      return apiService.get(`getUploadConflicts`, {
        params: {
          ...params,
          address: params.address || undefined,
          cluster: params.cluster || undefined,
        },
        baseURL: getFinalBaseUrl(baseUrl)
      })
    } catch (error) {
      console.error(error)
    }
  },

  async getConflictsTotal({ commit }, { type, coin, baseUrl }) {
    try {
      return apiService.get(`getConflictsTotal`, {
        params: { type, coin },
        baseURL: getFinalBaseUrl(baseUrl)
      })
    } catch (error) {
      console.error(error)
    }
  },

  async resolveConflict({ commit }, { baseUrl, ...data }) {
    try {
      return apiService.post(`resolveConflict`, data, { baseURL: getFinalBaseUrl(baseUrl) })
    } catch (error) {
      console.error(error)
    }
  },

  async deleteConflict({ commit }, { baseUrl, ...data }) {
    try {
      return apiService.delete(`deleteConflict`, {
        data,
        baseURL: getFinalBaseUrl(baseUrl)
      })
    } catch (error) {
      console.error(error)
    }
  },

  async getTags({ commit }, { baseUrl, ...params } ) {
    try {
      const path = featureAccess('MULTI_LABELING') ? '/labeling/getTags' : 'getTags'
      const fallback = featureAccess('MULTI_LABELING')
        ? appConfig.VUE_APP_LABEL_API_URL
        : (featureAccess('MULTI_LABELING') ? analytics.state.coinData.profilesServiceUrl : null)

      const finalParams = {
        count: 1000,
        ...params
      }

      return apiService.get(path, {
        params: finalParams,
        baseURL: baseUrl || fallback
      })
    } catch (error) {
      console.error(error)
    }
  },

  async getOwners({ commit }, { owner, $limit, baseUrl } ) {
    const isMulti = featureAccess('MULTI_LABELING')

    const path = isMulti
      ? (baseUrl ? '/labeling/findOwners' : '/wallet-labeling/findOwners' )
      : 'findOwners'

    const fallback = isMulti ? undefined : analytics.state.coinData.baseUrl

    const finalBaseUrl = baseUrl ||
      (isMulti ? appConfig.VUE_APP_LABEL_API_URL : fallback)

    try {
      return apiService.get(path, {
        params: {
          owner: owner || undefined,
          $limit: $limit || undefined
        },
        baseURL: finalBaseUrl
      })
    } catch (error) {
      console.error(error)
    }
  },

  async getClusterInfo({ commit }, clusterId) {
    try {
      const data = apiService.get(`explorer/getClusterInfo/${clusterId}`)
      return data
    } catch (error) {
      console.error(error)
    }
  },

  async getAddressTags({ commit }, params) {
    const address = typeof params === 'object' ? params.address : params;
    const baseUrl = params?.baseUrl;
    const coin = params?.coin

    const query = coin ? `?coin=${coin}` : ''

    try {
      return apiService.get(`getAddressTags/${address}${query}`, { baseURL: getFinalBaseUrl(baseUrl) })
    } catch (error) {
      console.error(error)
    }
  },

  // async getClusterConflicts({ commit }, { baseUrl, ...params }) {
  //   try {
  //     return apiService.get(`mergedClusterInfo`, {
  //       params,
  //       baseURL: getFinalBaseUrl(baseUrl)
  //     })
  //   } catch (error) {
  //     console.error(error)
  //   }
  // },

  async getClusterConflicts({ commit }, params) {
    const count = params?.count
    const skip = params?.skip
    const baseUrl = params?.baseUrl;

    try {
      const data = apiService.get(`labeling/mergedClusterInfo`, { baseURL: getFinalBaseUrl(baseUrl), params: { count, skip } })
      return data
    } catch (error) {
      console.error(error)
    }
  },

  async resolveClusterConflicts({ commit }, params) {
    const cluster = typeof params === 'object' ? params.cluster : params
    const baseUrl = params?.baseUrl

    try {
      return apiService.get(`resolveMergedCluster/${cluster}`, { baseURL: getFinalBaseUrl(baseUrl) })
    } catch (error) {
      console.error(error)
    }
  },

  async generateClusterInfo({ commit }, params = {}) {
    const baseUrl = params?.baseUrl

    try {
      return apiService.get(`labeling/generateClusterInfo`, { baseURL: getFinalBaseUrl(baseUrl) })
    } catch (error) {
      console.error(error)
    }
  },

  async addTag({ commit }, { baseUrl, ...data }) {
    if (data.name) data.name = sendFormatter(data.name);

    try {
      return apiService.post(`addTag`, data, { baseURL: getFinalBaseUrl(baseUrl) })
    } catch (error) {
      console.error(error)
    }
  },

  async editTag({ commit }, { baseUrl, isScoringPage = false, ...params }) {
    const { name, newName, ...rest } = params

    const formattedName = sendFormatter(name)
    const formattedNewName = sendFormatter(newName)

    const isMulti = featureAccess('MULTI_LABELING')

    const path = isMulti
      ? (isScoringPage ? '/wallet-labeling/editTag' : '/labeling/editTag')
      : 'editTag'

    const fallback = isMulti ? undefined : analytics.state.coinData.baseUrl

    const finalBaseUrl = baseUrl ||
      (isMulti ? appConfig.VUE_APP_LABEL_API_URL : fallback)

    const payload = {
      ...rest,
      name: formattedName,
      newName: formattedNewName
    }

    try {
      const data = await apiService.post(path, payload, {
        baseURL: finalBaseUrl
      })
      return data
    } catch (error) {
      console.error(error)
    }
  },

  deleteTag(ctx, { names, ids, skipCheck, baseUrl }) {
    const formattedNames = names ? names.map(name => sendFormatter(name)) : [];

    return apiService.delete(`deleteTag`, {
      data: { names: formattedNames, ids, skipCheck },
      baseURL: getFinalBaseUrl(baseUrl)
    });
  },

  deleteAddressTag(ctx, { name, address, baseUrl }) {
    const formattedName = sendFormatter(name)
    return apiService.delete(`deleteAddressTag/${address}/${formattedName}`, { baseURL: getFinalBaseUrl(baseUrl) })
  },

  deleteClusterTag(ctx, { name, address, baseUrl }) {
    const formattedName = sendFormatter(name)
    return apiService.delete(`deleteClusterTag/${address}/${formattedName}`, { baseURL: getFinalBaseUrl(baseUrl) })
  },

  async getAddressLabelingInfo({ commit }, params) {
    const address = typeof params === 'object' ? params.address : params
    const coin = params?.coin
    const useCommonFallback = params?.useCommonFallback

    let query = coin ? `?coin=${coin}` : ''
    query += useCommonFallback && coin ? `${query ? '&' : '?'}useCommonFallback=false` : ''

    const response = await apiService.get(
      `getAddressLabelingInfo/${address}${query}`,
      { baseURL: appConfig.VUE_APP_WALLET_LABEL_API_URL }
    )

    return {
      success: true,
      data: response
    }
  },

  async getAddressLabeledCoins(ctx, address) {
    try {
      const { data } = await apiService.get(
        `getAddressLabeledCoins/${address}`,
        { baseURL: appConfig.VUE_APP_WALLET_LABEL_API_URL }
      )

      return { coins: data?.coins || [], common: Boolean(data?.common) }
    } catch {
      return { coins: [], common: false }
    }
  },

  async getClusterLabelInfo({ commit }, params) {
    const cluster = typeof params === 'object' ? params.cluster : params
    const coin = params?.coin
    const useCommonFallback = params?.useCommonFallback

    let query = coin ? `?coin=${coin}` : ''
    query += useCommonFallback && coin ? `${query ? '&' : '?'}useCommonFallback=false` : ''

    return apiService.get(
      `getClusterLabelInfo/${cluster}${query}`,
      { baseURL: appConfig.VUE_APP_WALLET_LABEL_API_URL }
    )
  },

  async getAddressWithClusterLabelingInfo({ dispatch }, params) {
    const address = typeof params === 'object' ? params.address : params
    const coin = params?.coin
    const isEth = params?.isEth
    const clusterId = params?.clusterId
    const useCommonFallback = params?.useCommonFallback

    let addressRes = {}
    let clusterRes = {}
    let clusterAnchorAddress = null
    let clusterSize = null
    let cluster = null

    if (clusterId) {
      const { data, success } = await dispatch('getClusterInfo', clusterId)

      if (!success) {
        return {
          success: false,
          data: data || {}
        }
      }

      clusterAnchorAddress = data.address
      clusterSize = data.addressCount
      cluster = data.cluster

      try {
        clusterRes = await dispatch('getClusterLabelInfo', {
          // address: data.address,
          cluster,
          coin,
          useCommonFallback
        })
      } catch (e) {
        if (e?.response?.status === 404 && !clusterSize) {
          clusterRes = 404
        }
      }
    }

    if (!clusterId) {
      try {
        addressRes = await dispatch('getAddressLabelingInfo', { address, coin, useCommonFallback })
      } catch (e) {
        if (e?.response?.status === 404) {
          addressRes = 404
        }
      }
    }

    if (!isEth && !clusterId && coin && coin !== 'all') {
      try {
        const { data, success } = await dispatch('analytics/getAddressData', { address }, { root: true })

        if (success && data.clusterData.clusterSize) {
          clusterAnchorAddress = data.clusterData.address
          clusterSize = data.clusterData?.clusterSize
          cluster = data.clusterData?.cluster

          clusterRes = await dispatch('getClusterLabelInfo', {
            // address: clusterAnchorAddress,
            cluster,
            coin,
            useCommonFallback
          })
        }
      } catch (e) {
        if (e?.response?.status === 404 && !clusterSize) {
          clusterRes = 404
        }
      }
    }

    if (clusterId ? clusterRes === 404 : addressRes === 404 && clusterRes === 404) {
      return { success: true, data: {} }
    }

    clusterRes.clusterAnchorAddress = clusterAnchorAddress
    clusterRes.clusterSize = clusterSize
    clusterRes.cluster = cluster

    return {
      success: true,
      data: {
        ...(addressRes?.data || {}),
        clusterData: clusterRes || {},
        address,
        cluster,
      },
    }
  },
}
